<template>
  <div style="height:320px; position: relative;">
    <div style="text-align:left !important;">
      <div v-if="!loading && hasData" class="chart-container">
        <canvas
          ref="chartCanvas"
          :width="canvasWidth"
          :height="canvasHeight"
          @mousemove="handleMouseMove"
          @mouseleave="handleMouseLeave"
        ></canvas>
        <div class="legend" :style="legendStyle">
          <div 
            v-for="(item, index) in legendItems" 
            :key="index"
            class="legend-item"
            :class="{ 'legend-item-hover': hoveredSegment === index }"
          >
            <span 
              class="legend-color" 
              :style="{ backgroundColor: item.color }"
            ></span>
            <span class="legend-text">{{ item.label }}: {{ item.value }}</span>
          </div>
        </div>
      </div>
      <div v-else-if="loading" class="loading-container">
        Cargando datos para el gráfico...
      </div>
      <div v-else class="error-container">
        {{ error || 'No data available' }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DPie',
  props: {
    id: { 
      type: String, 
      default: () => `dpie-${Math.random().toString(36).substr(2, 9)}`
    },
    source: { type: String, required: true },
    title: { type: String, default: '' },
    type: { type: String, default: 'doughnut' }, // doughnut, pie, radial
    colors: { type: Array, default: () => ['#1ab7ea', '#0084ff', '#39539E', '#0077B5'] },
    colorsByLabel: { type: Object, default: () => ({}) },
    showLastModification: { type: Boolean, default: false },
    start: { type: Number, default: 0 },
    end: { type: Number, default: 0 },
    range: { type: Boolean, default: false }
  },
  data() {
    return {
      appConfig: process.env.NODE_ENV === 'production'
        ? require('@src/app.config')
        : require('@src/app.config.dev'),
      chartData: {
        labels: [],
        data: [],
        colors: []
      },
      loading: false,
      error: null,
      hasData: false,
      canvasWidth: 600,
      canvasHeight: 295,
      hoveredSegment: -1,
      segments: []
    }
  },
  computed: {
    legendItems() {
      return this.chartData.labels.map((label, index) => ({
        label,
        value: this.chartData.data[index],
        color: this.chartData.colors[index]
      }))
    },
    legendStyle() {
      return {
        position: 'absolute',
        left: '30px', // Increased from 20px
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        width: '35%',
        maxWidth: '140px'
      }
    },
    total() {
      return this.chartData.data.reduce((sum, value) => sum + value, 0)
    }
  },
  watch: {
    source: {
      handler() {
        this.getData()
      },
      immediate: false
    },
    start() {
      if (this.range) this.getData()
    },
    end() {
      if (this.range) this.getData()
    },
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.drawChart()
        })
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.getData()
    })
  },
  methods: {
    getData() {
      if (!this.source) {
        console.error('DPie Error - source not defined')
        this.error = 'Source not defined'
        return
      }

      if (this.loading) {
        return
      }

      this.loading = true
      this.error = null

      let url = this.source
      if (this.range) {
        if (this.start > 0) url += '&start=' + this.start
        if (this.end > 0) url += '&end=' + this.end
      }

      const baseURL = (this.appConfig.xdrAPI && this.appConfig.xdrAPI.baseURL) || 'http://localhost:5000'
      
      axios.get(baseURL + url)
        .then((resp) => {
          if (resp.status !== 200) {
            if (resp.status === 404) {
              console.error('DPie Error - source not found')
              this.error = 'Source not found'
              this.$emit('finished', this.id, false, 'source not found')
            }
            return
          }
          
          const payload = resp.data
          if (!Array.isArray(payload.data) || !payload.updated) {
            console.error('DPie Error - malformed response')
            this.error = 'Malformed response'
            return
          }

          this.processChartData(payload.data)
          this.$emit('finished', this.id, true, `Series Length: ${this.chartData.data.length}`)
        })
        .catch((err) => {
          console.error('Error loading DPie data:', err.response || err)
          this.error = 'Failed to load data'
          this.$emit('finished', this.id, false, err.response || err)
          
          const status = err.response && err.response.status
          if (status === 401 || status === 403) {
            if (typeof localStorage !== 'undefined') {
              localStorage.removeItem('idToken')
            }
            if (this.$router) {
              this.$router.push({ name: 'Home' })
            }
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    processChartData(data) {
      const labels = []
      const colors = []
      const counts = []
      let colorIndex = 0

      data.forEach((entry) => {
        // Process label
        let label = ''
        if (typeof entry.label === 'string') {
          label = entry.label.replace(/_/g, ' ')
        }
        labels.push(label)

        // Process color
        if (this.colorsByLabel && Object.prototype.hasOwnProperty.call(this.colorsByLabel, entry.label)) {
          colors.push(this.colorsByLabel[entry.label])
        } else {
          colors.push(this.colors[colorIndex])
          colorIndex = (colorIndex + 1) % this.colors.length
        }

        // Process count
        const count = (entry.count !== undefined && entry.count !== null) ? entry.count : 0
        counts.push(count)
      })

      // Update chart data
      this.chartData = {
        labels: labels,
        data: counts,
        colors: colors
      }

      this.hasData = counts.length > 0 && counts.some(count => count > 0)
      
      // Force chart redraw
      if (this.hasData) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.drawChart()
          }, 100)
        })
      }
    },

    drawChart() {
      const canvas = this.$refs.chartCanvas
      if (!canvas || !this.hasData) return

      const ctx = canvas.getContext('2d')
      
      // Set canvas size properly
      const dpr = window.devicePixelRatio || 1
      canvas.width = this.canvasWidth * dpr
      canvas.height = this.canvasHeight * dpr
      canvas.style.width = this.canvasWidth + 'px'
      canvas.style.height = this.canvasHeight + 'px'
      ctx.scale(dpr, dpr)
      
      // Better centered positioning - chart takes right 60% of space
      const chartAreaStart = this.canvasWidth * 0.4 // Legend takes left 40%
      const chartAreaWidth = this.canvasWidth * 0.6 // Chart takes right 60%
      const centerX = chartAreaStart + (chartAreaWidth / 2) + 20 // Move 20px to the right
      const centerY = this.canvasHeight / 2
      const radius = Math.min(chartAreaWidth - 40, this.canvasHeight - 40) / 2

      // Clear canvas
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)

      if (this.type === 'radial') {
        this.drawRadialChart(ctx, centerX, centerY, radius)
      } else {
        this.drawPieChart(ctx, centerX, centerY, radius)
      }
    },

    drawPieChart(ctx, centerX, centerY, radius) {
      let currentAngle = -Math.PI / 2 // Start from top
      this.segments = []

      this.chartData.data.forEach((value, index) => {
        const sliceAngle = (value / this.total) * 2 * Math.PI
        const endAngle = currentAngle + sliceAngle

        // Store segment info for hover detection
        this.segments.push({
          startAngle: currentAngle,
          endAngle: endAngle,
          centerX,
          centerY,
          radius: this.type === 'doughnut' ? radius : radius,
          innerRadius: this.type === 'doughnut' ? radius * 0.4 : 0
        })

        // Draw slice
        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, currentAngle, endAngle)
        
        if (this.type === 'doughnut') {
          ctx.arc(centerX, centerY, radius * 0.4, endAngle, currentAngle, true)
        } else {
          ctx.lineTo(centerX, centerY)
        }
        
        ctx.closePath()
        
        // Apply hover effect
        if (this.hoveredSegment === index) {
          ctx.fillStyle = this.lightenColor(this.chartData.colors[index], 20)
        } else {
          ctx.fillStyle = this.chartData.colors[index]
        }
        
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.stroke()

        currentAngle = endAngle
      })
    },

    drawRadialChart(ctx, centerX, centerY, maxRadius) {
      const maxValue = Math.max(...this.chartData.data)
      const radiusStep = maxRadius / this.chartData.data.length
      
      this.segments = []

      this.chartData.data.forEach((value, index) => {
        const radius = (value / maxValue) * maxRadius
        const startAngle = -Math.PI / 2
        const endAngle = startAngle + (270 * Math.PI / 180) // 270 degrees

        this.segments.push({
          startAngle,
          endAngle,
          centerX,
          centerY,
          radius,
          innerRadius: radius * 0.3
        })

        // Draw radial bar
        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, startAngle, endAngle)
        ctx.arc(centerX, centerY, radius * 0.3, endAngle, startAngle, true)
        ctx.closePath()

        if (this.hoveredSegment === index) {
          ctx.fillStyle = this.lightenColor(this.chartData.colors[index], 20)
        } else {
          ctx.fillStyle = this.chartData.colors[index]
        }
        
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 1
        ctx.stroke()
      })
    },

    handleMouseMove(event) {
      const canvas = this.$refs.chartCanvas
      const rect = canvas.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top

      let hoveredIndex = -1

      this.segments.forEach((segment, index) => {
        if (this.isPointInSegment(x, y, segment)) {
          hoveredIndex = index
        }
      })

      if (hoveredIndex !== this.hoveredSegment) {
        this.hoveredSegment = hoveredIndex
        this.drawChart()
      }
    },

    handleMouseLeave() {
      if (this.hoveredSegment !== -1) {
        this.hoveredSegment = -1
        this.drawChart()
      }
    },

    isPointInSegment(x, y, segment) {
      const dx = x - segment.centerX
      const dy = y - segment.centerY
      const distance = Math.sqrt(dx * dx + dy * dy)
      const angle = Math.atan2(dy, dx)

      // Normalize angle to match our coordinate system
      let normalizedAngle = angle + Math.PI / 2
      if (normalizedAngle < 0) normalizedAngle += 2 * Math.PI

      const isInRadiusRange = distance >= segment.innerRadius && distance <= segment.radius
      const isInAngleRange = normalizedAngle >= segment.startAngle && normalizedAngle <= segment.endAngle

      return isInRadiusRange && isInAngleRange
    },

    lightenColor(color, percent) {
      // Simple color lightening function
      const num = parseInt(color.replace('#', ''), 16)
      const amt = Math.round(2.55 * percent)
      const R = (num >> 16) + amt
      const G = (num >> 8 & 0x00FF) + amt
      const B = (num & 0x0000FF) + amt
      return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1)
    },

    refresh() {
      this.getData()
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  height: 295px;
  display: flex;
  align-items: center;
}

.legend {
  width: 35%;
  max-width: 500px;
  min-width: 300px;
  padding-left: 20px;
  padding-right: 15px;
}

.legend-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  padding: 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
  cursor: pointer;
  line-height: 1.3;
}

.legend-item-hover {
  background-color: #f5f5f5;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
  margin-top: 2px;
  flex-shrink: 0;
}

.legend-text {
  font-size: 11px;
  line-height: 1.3;
  color: #333;
  word-wrap: break-word;
  text-align: left;
  flex: 1;
}

.loading-container,
.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 295px;
  color: #666;
}

.error-container {
  color: #d32f2f;
}

canvas {
  cursor: pointer;
  position: absolute;
  left: 0;
  top: 0;
}
</style>